import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  IconButton,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GoogleMap, Circle, Polygon, InfoWindow } from '@react-google-maps/api';
import { PlayArrow, Pause } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import axios from '../../../../resources/axios';
import { PlaybackData, PlaybackScooter, AppOpenData, AppOpenCoordinate } from './types';
import { City } from '../../../../resources/types/citiesTypes';
import ScooterMarkerWithBadge from '../../../../containers/MapMarker/ScooterMarkerWithBadge';
import { getScooterIcon } from '../../../../helpers/scooter-markers-helper';
import { isScooterActon } from '../../../../helpers/scooter-helper';
import { Scooter } from '../../../../resources/types/scootersTypes';
import { Zone } from '../../../../resources/types/zonesTypes';
import { selectZones, getZonesAsync } from '../../../../redux/slices/zonesSlice';
import { zonesColors } from '../../../ZonesPage/constants';
import { MapFilters, ScooterStatus } from '../../../ZonesPage/interface';
import PlaybackScooterCounters from './PlaybackScooterCounters';

interface PlaybackMapViewProps {
  appOpenData: AppOpenData | null;
  selectedCity: City | undefined;
  selectedDate: Moment | null;
}

const PlaybackMapView: React.FC<PlaybackMapViewProps> = ({ appOpenData, selectedCity, selectedDate }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const zonesState = useSelector(selectZones);
  
  const [dateFrom, setDateFrom] = useState<Moment | null>(null);
  const [dateTo, setDateTo] = useState<Moment | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [showAppOpens, setShowAppOpens] = useState(true);
  const [showZones, setShowZones] = useState(true);
  const [playbackData, setPlaybackData] = useState<PlaybackData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<{
    position: google.maps.LatLng | null;
    zone: Zone | null;
  }>({ position: null, zone: null });
  const [mapFilters, setMapFilters] = useState<MapFilters>({});

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  useEffect(() => {
    dispatch(getZonesAsync(true) as any);
  }, [dispatch]);

  // Sync dates with parent component's selectedDate
  useEffect(() => {
    if (selectedDate) {
      setDateFrom(selectedDate.clone().startOf('day'));
      setDateTo(selectedDate.clone().endOf('day'));
    }
  }, [selectedDate]);

  const filteredAppOpenCoords = useMemo(() => {
    if (!appOpenData?.coords || !selectedCity?.lat || !selectedCity?.lon) {
      return [];
    }

    const MAX_DISTANCE_KM = 15;
    return appOpenData.coords.filter(coord => {
      const distance = calculateDistance(selectedCity.lat!, selectedCity.lon!, coord.lat, coord.lon);
      return distance <= MAX_DISTANCE_KM;
    });
  }, [appOpenData, selectedCity]);

  const toggleInfoWindow = useCallback(
    (zone: Zone | null, e: google.maps.MapMouseEvent | null) => {
      setSelectedZone({ position: e ? e.latLng : null, zone });
    },
    []
  );

  const fetchPlaybackData = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      return;
    }

    const hoursDiff = dateTo.diff(dateFrom, 'hours', true);
    if (hoursDiff > 168) {
      setError('Максимальний період - 7 днів');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get('/scooters/updates_snapshot', {
        params: {
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString()
        }
      });

      if (response.data?.data) {
        setPlaybackData(response.data.data);
        setCurrentTime(0);
      }
    } catch (err: any) {
      console.error('PlaybackMapView API Error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.url,
        params: err.config?.params,
        headers: err.config?.headers
      });
      
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Помилка завантаження даних';
      setError(`${errorMessage} (статус: ${err.response?.status || 'невідомий'})`);
      if (err.response?.status === 404) {
        console.warn('Endpoint /scooters/updates_snapshot not found');
      }
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo]);

  const allSnapshots = useMemo(() => {
    if (!playbackData) return [];
    return [playbackData.initial, ...playbackData.snapshots];
  }, [playbackData]);

  const scooterStatesAtTime = useMemo((): PlaybackScooter[] => {
    if (!playbackData || allSnapshots.length === 0) return [];

    const scooterMap = new Map<string, PlaybackScooter>();

    playbackData.initial.scooters.forEach(scooter => {
      if (scooter.coords) {
        const scooterName = scooter.name || '';
        scooterMap.set(scooter.sc, {
          id: scooter.sc,
          name: scooterName,
          coords: scooter.coords,
          connected: scooter.connected ?? true,
          battery: scooter.battery ?? 0,
          status: scooter.status || 'AVAILABLE',
          lock: scooter.lock ?? true,
          isReplaceableBattery: scooter.isReplaceableBattery ?? isScooterActon(scooterName)
        });
      }
    });

    for (let i = 0; i < currentTime && i < playbackData.snapshots.length; i++) {
      const snapshot = playbackData.snapshots[i];
      snapshot.scooters.forEach(update => {
        const existing = scooterMap.get(update.sc);
        const updatedName = update.name !== undefined ? update.name : existing?.name || '';
        
        if (existing) {
          const updatedScooter = {
            ...existing,
            ...(update.coords && { coords: update.coords }),
            ...(update.connected !== undefined && { connected: update.connected }),
            ...(update.name !== undefined && { name: update.name }),
            ...(update.battery !== undefined && { battery: update.battery }),
            ...(update.status !== undefined && { status: update.status }),
            ...(update.lock !== undefined && { lock: update.lock })
          };
          
          scooterMap.set(update.sc, {
            ...updatedScooter,
            isReplaceableBattery: update.isReplaceableBattery ?? isScooterActon(updatedScooter.name)
          });
        } else if (update.coords) {
          const scooterName = update.name || '';
          scooterMap.set(update.sc, {
            id: update.sc,
            name: scooterName,
            coords: update.coords,
            connected: update.connected ?? true,
            battery: update.battery ?? 0,
            status: update.status || 'AVAILABLE',
            lock: update.lock ?? true,
            isReplaceableBattery: update.isReplaceableBattery ?? isScooterActon(scooterName)
          });
        }
      });
    }

    return Array.from(scooterMap.values());
  }, [playbackData, allSnapshots, currentTime]);

  const appOpensAtTime = useMemo((): AppOpenCoordinate[] => {
    if (!showAppOpens || !playbackData || allSnapshots.length === 0) return [];
    
    const currentSnapshot = allSnapshots[currentTime];
    if (!currentSnapshot) return [];

    const snapshotTime = moment(currentSnapshot.ts);
    
    return filteredAppOpenCoords.filter(coord => {
      const coordTime = moment(coord.createdAt);
      return coordTime.isSameOrBefore(snapshotTime);
    });
  }, [filteredAppOpenCoords, playbackData, allSnapshots, currentTime, showAppOpens]);

  const mapCenter = useMemo((): google.maps.LatLngLiteral => {
    if (selectedCity?.lat && selectedCity?.lon) {
      return { lat: selectedCity.lat, lng: selectedCity.lon };
    }
    if (scooterStatesAtTime.length > 0) {
      const lat = scooterStatesAtTime.reduce((sum, s) => sum + s.coords.lat, 0) / scooterStatesAtTime.length;
      const lng = scooterStatesAtTime.reduce((sum, s) => sum + s.coords.lon, 0) / scooterStatesAtTime.length;
      return { lat, lng };
    }
    return { lat: 48.9226, lng: 24.7111 };
  }, [selectedCity, scooterStatesAtTime]);

  const mapOptions = useMemo(
    (): google.maps.MapOptions => ({
      styles: theme.palette.mode === 'dark'
        ? [
            { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
            {
              featureType: 'administrative.locality',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#d59563' }]
            },
            {
              featureType: 'poi',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#d59563' }]
            },
            {
              featureType: 'poi.park',
              elementType: 'geometry',
              stylers: [{ color: '#263c3f' }]
            },
            {
              featureType: 'poi.park',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#6b9a76' }]
            },
            {
              featureType: 'road',
              elementType: 'geometry',
              stylers: [{ color: '#38414e' }]
            },
            {
              featureType: 'road',
              elementType: 'geometry.stroke',
              stylers: [{ color: '#212a37' }]
            },
            {
              featureType: 'road',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#9ca5b3' }]
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry',
              stylers: [{ color: '#746855' }]
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry.stroke',
              stylers: [{ color: '#1f2835' }]
            },
            {
              featureType: 'road.highway',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#f3d19c' }]
            },
            {
              featureType: 'transit',
              elementType: 'geometry',
              stylers: [{ color: '#2f3948' }]
            },
            {
              featureType: 'transit.station',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#d59563' }]
            },
            {
              featureType: 'water',
              elementType: 'geometry',
              stylers: [{ color: '#17263c' }]
            },
            {
              featureType: 'water',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#515c6d' }]
            },
            {
              featureType: 'water',
              elementType: 'labels.text.stroke',
              stylers: [{ color: '#17263c' }]
            }
          ]
        : [],
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: true,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true
    }),
    [theme.palette.mode]
  );

  const convertPlaybackScooterToScooter = useCallback((playbackScooter: PlaybackScooter): Partial<Scooter> => {
    const status = playbackScooter.status?.toUpperCase() || 'AVAILABLE';
    const isInRide = status === 'RIDE' || status === 'RESERVED';
    
    return {
      _id: playbackScooter.id,
      name: playbackScooter.name,
      battery: playbackScooter.battery,
      connected: playbackScooter.connected,
      status: playbackScooter.status,
      lock: playbackScooter.lock,
      coords: {
        lat: playbackScooter.coords.lat,
        lon: playbackScooter.coords.lon,
        updatedAt: ''
      },
      free: !isInRide,
      viewed: true,
      isReplaceableBattery: playbackScooter.isReplaceableBattery
    } as Scooter;
  }, []);

  const filteredScootersAtTime = useMemo((): PlaybackScooter[] => {
    let filtered = scooterStatesAtTime;
    const filters = mapFilters ?? {};

    if (Object.keys(filters).length === 0) {
      return filtered;
    }

    if (filters.free !== undefined) {
      const isFree = filters.free === true || filters.free === 'all';
      if (filters.free === 'all') {
        return filtered;
      }
      filtered = filtered.filter(scooter => {
        const status = scooter.status?.toUpperCase() || 'AVAILABLE';
        const scooterFree = !['RESERVED', 'RIDE'].includes(status);
        return isFree ? scooterFree : !scooterFree;
      });
    }

    if (filters.unlocked) {
      filtered = filtered.filter(scooter => !scooter.lock);
    }

    if (filters.offline) {
      filtered = filtered.filter(scooter => !scooter.connected);
    }

    if (filters.status) {
      const statuses = filters.status.split(',').filter(s => s);
      if (statuses.length > 0) {
        filtered = filtered.filter(scooter => statuses.includes(scooter.status));
      }
    }

    return filtered;
  }, [scooterStatesAtTime, mapFilters]);

  useEffect(() => {
    if (isPlaying && allSnapshots.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= allSnapshots.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000 / playbackSpeed);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, allSnapshots.length]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (_event: Event, value: number | number[]) => {
    setCurrentTime(value as number);
    setIsPlaying(false);
  };

  const handleSpeedChange = (_event: React.MouseEvent<HTMLElement>, newSpeed: number | null) => {
    if (newSpeed !== null) {
      setPlaybackSpeed(newSpeed);
    }
  };

  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (newValue) {
      const newDate = moment(newValue).startOf('day');
      setDateFrom(newDate);
      if (dateTo && newDate.isAfter(dateTo)) {
        setDateTo(newDate.clone().endOf('day'));
      }
    }
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (newValue) {
      const newDate = moment(newValue).endOf('day');
      setDateTo(newDate);
      if (dateFrom && newDate.isBefore(dateFrom)) {
        setDateFrom(newDate.clone().startOf('day'));
      }
    }
  };

  const currentTimestamp = allSnapshots[currentTime]?.ts;

  return (
    <Box>
      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
          borderRadius: 2
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Дата від"
            type="date"
            value={dateFrom ? dateFrom.format('YYYY-MM-DD') : ''}
            onChange={handleDateFromChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: moment().format('YYYY-MM-DD') }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Дата до"
            type="date"
            value={dateTo ? dateTo.format('YYYY-MM-DD') : ''}
            onChange={handleDateToChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: moment().format('YYYY-MM-DD') }}
            sx={{ minWidth: 200 }}
          />
          <IconButton
            onClick={fetchPlaybackData}
            disabled={isLoading || !dateFrom || !dateTo}
            sx={{
              backgroundColor: '#5073F8',
              color: '#FFF',
              '&:hover': { backgroundColor: '#3D5FD9' },
              '&:disabled': { backgroundColor: '#E0E0E0' }
            }}
            title="Завантажити дані"
          >
            {isLoading ? <CircularProgress size={24} sx={{ color: '#FFF' }} /> : <PlayArrow />}
          </IconButton>
          <FormControlLabel
            control={
              <Checkbox
                checked={showAppOpens}
                onChange={(e) => setShowAppOpens(e.target.checked)}
                sx={{ color: '#9C27B0' }}
              />
            }
            label="Відкриття додатку"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showZones}
                onChange={(e) => setShowZones(e.target.checked)}
              />
            }
            label="Зони"
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {playbackData && allSnapshots.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
            borderRadius: 2
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                onClick={handlePlayPause}
                sx={{
                  backgroundColor: isPlaying ? '#F44336' : '#4CAF50',
                  color: '#FFF',
                  '&:hover': {
                    backgroundColor: isPlaying ? '#D32F2F' : '#388E3C'
                  }
                }}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>

              <ToggleButtonGroup
                value={playbackSpeed}
                exclusive
                onChange={handleSpeedChange}
                size="small"
              >
                <ToggleButton value={1}>1x</ToggleButton>
                <ToggleButton value={2}>2x</ToggleButton>
                <ToggleButton value={4}>4x</ToggleButton>
                <ToggleButton value={8}>8x</ToggleButton>
                <ToggleButton value={16}>16x</ToggleButton>
              </ToggleButtonGroup>

              <Typography variant="body2" sx={{ ml: 'auto', minWidth: 150 }}>
                {currentTimestamp && moment(currentTimestamp).format('DD.MM.YYYY HH:mm:ss')}
              </Typography>
            </Stack>

            <Box sx={{ px: 2 }}>
              <Slider
                value={currentTime}
                onChange={handleSliderChange}
                min={0}
                max={Math.max(0, allSnapshots.length - 1)}
                step={1}
                marks
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => {
                  const snapshot = allSnapshots[value];
                  return snapshot ? moment(snapshot.ts).format('HH:mm:ss') : '';
                }}
                sx={{
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#5073F8'
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#5073F8'
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'
                  }
                }}
              />
            </Box>

            <Stack direction="row" spacing={3} sx={{ px: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Кадр: {currentTime + 1} / {allSnapshots.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Самокатів: {filteredScootersAtTime.length} / {scooterStatesAtTime.length}
              </Typography>
              {showAppOpens && (
                <Typography variant="body2" color="textSecondary" sx={{ color: '#9C27B0' }}>
                  Відкриттів: {appOpensAtTime.length}
                </Typography>
              )}
            </Stack>
            
            <Box sx={{ px: 2, mt: 2 }}>
              <PlaybackScooterCounters
                scooters={scooterStatesAtTime}
                mapFilters={mapFilters}
                onFilterSet={setMapFilters}
              />
            </Box>
          </Stack>
        </Paper>
      )}

      <Paper
        sx={{
          height: 600,
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={13}
          options={mapOptions}
        >
          {filteredScootersAtTime.map((scooter) => {
            const convertedScooter = convertPlaybackScooterToScooter(scooter);
            return (
              <ScooterMarkerWithBadge
                key={scooter.id}
                position={{ lat: scooter.coords.lat, lng: scooter.coords.lon }}
                icon={{
                  url: getScooterIcon(convertedScooter as Scooter, scooter.name)!,
                  labelOrigin: new window.google.maps.Point(16, 15)
                }}
                batteryLevel={String(scooter.battery)}
              />
            );
          })}

          {showAppOpens && appOpensAtTime.map((coord, index) => (
            <Circle
              key={`app-open-${index}-${coord.createdAt}`}
              center={{ lat: coord.lat, lng: coord.lon }}
              radius={40}
              options={{
                fillColor: '#9C27B0',
                fillOpacity: 0.6,
                strokeColor: '#9C27B0',
                strokeOpacity: 0.8,
                strokeWeight: 1
              }}
            />
          ))}

          {showZones && zonesState.zones.map(zone => (
            <Polygon
              key={zone._id}
              options={zonesColors[zone.type] || zonesColors.green}
              paths={zone.coordinates.map(coordinate => ({
                lat: coordinate[0],
                lng: coordinate[1]
              }))}
              onClick={e => toggleInfoWindow(zone, e)}
            />
          ))}

          {selectedZone.zone && selectedZone.position && (
            <InfoWindow
              position={selectedZone.position}
              onCloseClick={() => toggleInfoWindow(null, null)}
            >
              <>
                Назва: {selectedZone.zone.name}
                <br />
                Тип: {selectedZone.zone.type}
              </>
            </InfoWindow>
          )}
        </GoogleMap>
      </Paper>

      {!playbackData && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E' }}>
            Виберіть період та натисніть кнопку для завантаження даних
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PlaybackMapView;
